import { createStore } from '@/utils/createStore'

export type EventTrigger = 
  | 'LEAD_ASSIGNED'
  | 'SLA_BREACH'
  | 'FOLLOWUP_DUE'
  | 'PROPOSAL_EXPIRY'
  | 'AGREEMENT_EXPIRY'
  | 'ALLOCATION_PENDING'
  | 'INVOICE_GENERATED'
  | 'INVOICE_DUE'
  | 'INVOICE_OVERDUE'
  | 'PAYMENT_RECEIVED'
  | 'APPROVAL_REJECTED'
  | 'SYSTEM_ALERT'

export type DeliveryChannel = 'Email' | 'WhatsApp' | 'In-App'
export type DeliveryStatus = 'Sent' | 'Queued' | 'Read' | 'Unread'

export interface NotificationItem {
  id: string
  eventTrigger: EventTrigger
  title: string
  message: string
  recipient: string
  channels: DeliveryChannel[]
  status: DeliveryStatus
  createdAt: string
}

// A notification is "unread" until the user explicitly reads it. Delivery
// states ('Sent' when delivered, 'Queued' off-hours) and the legacy 'Unread'
// value all mean "not yet read". A missing/blank status is treated as unread
// too, so a malformed row is surfaced rather than silently hidden.
//
// This is the SINGLE source of truth for the unread rule — the bell badge
// (MainLayout) and the Notifications page must both use it so they can never
// disagree (previously the bell counted !== 'Read' while the page only counted
// === 'Unread', so delivered notifications showed a number the page called 0).
export const isUnread = (status?: DeliveryStatus | string | null): boolean =>
  status !== 'Read'

/** Recipient value meaning "everyone" — not addressed to any one person. */
export const RECIPIENT_ALL = 'All'
/** Recipient value meaning "any Admin user". */
export const RECIPIENT_ADMIN = 'Admin'

/**
 * Is this notification addressed to the given user?
 *
 * SINGLE source of truth for notification visibility — the bell badge
 * (MainLayout) and the Notifications page must both use it, for the same
 * reason isUnread() exists: two independent rules drift and start disagreeing.
 *
 * Previously there was no rule at all — the page listed every row in the table,
 * so a notification addressed to one salesperson was visible to everyone, and
 * an admin-only approval request would have been too.
 *
 * Rules:
 *  · Admins see everything (they action the approval queue).
 *  · 'All' is broadcast — everyone sees it.
 *  · 'Admin' is admin-only — covered by the admin rule above.
 *  · Anything else matches on the user's name, case-insensitively.
 */
export const isForUser = (
  n: Pick<NotificationItem, 'recipient'>,
  user: { name?: string; role?: string } | null,
  isAdmin: boolean,
): boolean => {
  if (!user) return false
  if (isAdmin) return true
  const to = String(n.recipient || '').trim().toLowerCase()
  if (!to || to === RECIPIENT_ALL.toLowerCase()) return true
  if (to === RECIPIENT_ADMIN.toLowerCase()) return false
  return to === String(user.name || '').trim().toLowerCase()
}

// No seed data — notifications come from the backend (PostgreSQL) only.
// resource='/notifications' enables write-through + refresh().
export const notificationStore = createStore<NotificationItem>('sp_notifications', [], 'N', 3, '/notifications')
